import { shortcuts as request, safeShortcuts as safe } from '@myua/api';
import { APIScenarioFactory } from '../general/apiSagaFactory';
import { ARCHIVE_EXTENSIONS, FONT_EXTENSIONS } from '../general/fontLicense/model';
import { NodeFlag, FontLicense } from '../general/model';
export * from '../general/apiSagaFactory';

export type Project = {
  id: number;
  name: string;
  has_creatives: boolean;
};

export type Category = {
  id: string;
  name: string;
  path: string[];
};

export type Tag = {
  id: number;
  name: string;
  asset_count: number;
  related_tag_ids: Tag['id'][];
  is_new: boolean;
};

export type TagSimple = Pick<Tag, 'id' | 'name'>;

export enum AssetPerm {
  read = 'read',
  download = 'download',
  delete = 'delete',
}

export type Asset = {
  id: number;
  name: string;
  created_at: string;
  updated_at: string;
  size: number;
  is_new: boolean;
  perms: AssetPerm[];
  download_url?: string | null;
  preview: { url: string; extension: string; is_custom: boolean } | null;
  thumbnail_url: string | null;
  creatives: { id: number; name: string }[];
  can_create_direct_download_link: boolean;
  flags: NodeFlag[];
  type: {
    extension: string;
    group: string;
    bg_color: string;
    font_color: string;
  };
  ai_search_score?: number;
  // --- Font license moderation (MYUA-16456) ---
  // Разрешено ли скачивание. Бэкенд присылает флаг на каждом ассете (и в списке, и в деталях);
  // для шрифтов под модерацией/отклонённых он приходит false. Отсутствие поля трактуем как true.
  downloading_allowed?: boolean;
  // Лицензии шрифтов приходят только в деталях ассета (GET /assets/{id}) и только у файлов,
  // которые их несут, поэтому опциональны. Т.к. AssetDetails = Asset & {...}, поля доступны
  // в деталях/каталоге.
  //
  // У архива со шрифтами лицензий несколько — по одной на шрифт внутри (различаются
  // `filename`), поэтому основное поле — список. `font_license` — исходный контракт на
  // одну лицензию; оставлен для совместимости, читать оба следует через getAssetLicenses.
  font_licenses?: FontLicense[] | null;
  font_license?: FontLicense | null;
};

export type Extension = {
  id: string;
  name: string;
};

export type Source = {
  id: string;
  name: string;
};

export type AssetDetails = Asset & {
  owner_email: string;
  owner_first_name: string;
  owner_last_name: string;
  project: Project;
  category: Category | null;
  source: Source | null;
  tags: TagSimple[];
  spine_exporter: string | null;
  related: AssetDetails[];
  path: string;
  links: AssetLink[];
  animation_preview_id: Asset['id'] | null;
  animation_archive_id: Asset['id'] | null;
};

export type AssetLink = { id: number; asset_id: Asset['id']; url: string; editable: boolean };

export type SharingLink = {
  id: string;
  name: string;
  creator_id: number;
  created_at: string;
  is_active: boolean;
  is_safety_share: boolean;
  email: string;
};

export const loadProjects = APIScenarioFactory(() =>
  request.get<Project[]>({ url: '/api/asset-lab/projects' }),
);

// --- Font license moderation (MYUA-16456) ---
// Расширения берём из общего модуля лицензий, чтобы список не разъезжался с тем,
// по которому детектится шрифт при загрузке (general/fontLicense/model.ts).
export { FONT_EXTENSIONS };

const assetExtension = (asset: Pick<Asset, 'type'>): string =>
  (asset.type?.extension ?? '').toLowerCase();

export const isFontAsset = (asset: Pick<Asset, 'type'>): boolean =>
  (FONT_EXTENSIONS as readonly string[]).includes(assetExtension(asset));

// Файл, который может нести лицензии шрифтов: сам шрифт либо архив со шрифтами внутри
// (бэкенд внутрь архива не смотрит, но лицензии привязаны к ноде архива и блокируют
// его скачивание так же, как у шрифта).
export const isLicensedFileAsset = (asset: Pick<Asset, 'type'>): boolean =>
  isFontAsset(asset) || (ARCHIVE_EXTENSIONS as readonly string[]).includes(assetExtension(asset));

// Лицензии шрифтов из ответа бэкенда (только в деталях ассета). У архива их несколько —
// по одной на шрифт внутри. Читаем список, с откатом на одиночное поле font_license.
export const getAssetLicenses = (asset: Asset): FontLicense[] => {
  if (asset.font_licenses?.length) return asset.font_licenses;
  return asset.font_license ? [asset.font_license] : [];
};

// Разрешено ли скачивание ассета. Решение принимает бэкенд (флаг downloading_allowed):
// для шрифтов под модерацией/отклонённых он приходит false. Отсутствие флага = разрешено.
export const isAssetDownloadAllowed = (asset: Pick<Asset, 'downloading_allowed'>): boolean =>
  asset.downloading_allowed !== false;

export const loadCategories = APIScenarioFactory((projectIds?: Project['id'][]) => {
  const query: Record<string, string> = {};
  if (projectIds?.length) {
    query.project_id = projectIds.join();
  }
  return request.get<Category[]>({ url: `api/asset-lab/categories`, query });
});

export type SearchApiParams = {
  search?: string;
  search_in_layers?: 'true';
  search_in_archives?: 'true';
  search_with_ai?: 'true';
  search_upload_id?: number;
};

export const urlSearchUpload = () => '/api/asset-lab/assets/search/get-search-upload-id';

export const getSearchUploadIdForSimilar = (asset_id: Asset['id']) =>
  safe.post<{ search_upload_id: number }>({
    url: `/api/asset-lab/assets/search/get-search-upload-id-for-similar`,
    payload: { asset_id },
  });

export const loadTags = APIScenarioFactory(
  ({
    projectIds,
    categoryId,
    extensionIds,
    showAll,
    showCreatives,
    creativeId,
    ...rest
  }: {
    projectIds?: Project['id'][];
    categoryId?: Category['id'];
    extensionIds?: Extension['id'][];
    showAll?: boolean;
    showCreatives?: boolean;
    creativeId?: Asset['id'];
  } & SearchApiParams) => {
    const query: Record<string, string | number> = { ...rest };
    if (projectIds?.length) {
      query.project_id = projectIds.join();
    }
    if (categoryId !== undefined) {
      query.category = categoryId;
    }
    if (extensionIds?.length) {
      query.extension = extensionIds.join();
    }
    if (showAll !== undefined) {
      query.show_all = String(showAll);
    }
    if (showCreatives !== undefined) {
      query.show_creatives = String(showCreatives);
    }
    if (creativeId !== undefined) {
      query.creative_id = String(creativeId);
    }
    return request.get<Tag[]>({ url: `api/asset-lab/tags`, query });
  },
);

export const bindTags = APIScenarioFactory(
  ({
    tag_ids,
    asset_ids,
    filter,
  }: {
    tag_ids: Tag['id'][];
    asset_ids?: Asset['id'][];
    filter?: LoadAssetsFilter;
  }) => request.post({ url: `api/asset-lab/tags/bind`, payload: { tag_ids, asset_ids, filter } }),
);

export const appendTag = APIScenarioFactory((name: string) =>
  request.post({ url: `api/asset-lab/tags/append`, payload: { name } }),
);

export type TotalInfoNullable = {
  read: { count: number | null; size: number | null };
  download: { count: number | null; size: number | null };
};
export type TotalInfo = {
  read: { count: number; size: number };
  download: { count: number; size: number };
};
export type AssetsData = { assets: Asset[]; total: TotalInfoNullable };
export type AssetsTotalState = { total: TotalInfoNullable; asset_ids: Asset['id'][] };

export type LoadAssetsFilter = {
  project_id?: string;
  category?: string;
  tag?: string;
  extension?: string;
  creative_id?: string;
} & SearchApiParams;

export type LoadAssetsOptions = LoadAssetsFilter & {
  offset?: number;
  orderBy?: string;
  ordering?: 'asc' | 'desc';
  limit?: number;
  show_creatives?: boolean;
};

export const loadAssets = APIScenarioFactory(
  ({ offset, orderBy, ordering, limit, show_creatives, ...rest }: LoadAssetsOptions) => {
    const query: Record<string, string | number | undefined> = { ...rest };
    if (offset !== undefined) {
      query.offset = String(offset);
    }
    if (orderBy !== undefined) {
      query.order_by = orderBy;
    }
    if (ordering !== undefined) {
      query.ordering = ordering;
    }
    if (limit !== undefined) {
      query.limit = String(limit);
    }
    if (show_creatives !== undefined) {
      query.show_creatives = String(show_creatives);
    }
    return request.get<AssetsData>({ url: `api/asset-lab/assets`, query });
  },
);

export const urlDownloadBulk = (assetIds: Asset['id'][], sharingId?: SharingLink['id']): string => {
  const url = new URL(
    sharingId ? '/api/asset-lab/sharing/assets/download' : '/api/asset-lab/assets/download/',
    window.location.origin,
  );
  url.searchParams.append('asset_ids', assetIds.join());
  if (sharingId) {
    url.searchParams.set('sharing_id', sharingId);
  }
  return url.href;
};

export type Settings = {
  can_download_bulk_google: boolean;
  // Per-instance gate for font-license validation (MYUA-16307/16308); see the
  // matching field on the Creative Hub Settings type. Absent -> treated as false.
  // NB: Asset Lab reads settings from /api/asset-lab/settings, not the crhub
  // endpoint — the backend exposes this flag in both so the gate matches across
  // apps. Were it ever dropped here, the font license flow would silently stay
  // disabled in Asset Lab regardless of the crhub setting.
  validate_font_license_before_uploading?: boolean;
  // Per-instance gate for Magic Studio (MYUA-16447). The backend turns the whole
  // AI feature on/off per deployment; the frontend combines it with its own
  // kill-switch (see magicStudio/config.ts + app/selectors.getMagicStudioEnabled).
  // Absent -> treated as false, so the feature stays hidden until the backend
  // explicitly enables it.
  magic_studio_enabled?: boolean;
  // Per-instance gate for the Demo welcome flow (MYUA-17010 / backend MYUA-17009):
  // own "Your space" first and selected by default, special link opens the upload
  // modal (see modules/demoWelcome). Absent -> treated as false, so behaviour on
  // other instances is unchanged.
  demo_welcome_mode_enabled?: boolean;
};
export const loadSettings = () => safe.get<Settings>({ url: `/api/asset-lab/settings` });

export const createGoogleBulkLink = (asset_ids: Asset['id'][], sharing_id?: SharingLink['id']) =>
  safe.post<{ task_id: string }>({
    url: sharing_id
      ? `/api/asset-lab/sharing/assets/direct-link-bulk/`
      : `/api/asset-lab/assets/direct-link-bulk/`,
    payload: { asset_ids, sharing_id },
  });

export const createGoogleBulkLinkPack = () =>
  safe.post<{ task_id: string }>({
    url: `/api/asset-lab/pack/direct-link-bulk/`,
  });

export const createGoogleBulkLinkSharingAll = (sharing_id: SharingLink['id']) =>
  safe.post<{ task_id: string }>({
    url: `/api/asset-lab/sharing/assets/direct-link-bulk/`,
    payload: { sharing_id },
  });

export const loadExtensions = APIScenarioFactory(
  ({ projectIds, categoryId }: { projectIds?: Project['id'][]; categoryId?: Category['id'] }) => {
    const query: Record<string, string> = {};
    if (projectIds?.length) {
      query.project_id = projectIds.join();
    }
    if (categoryId !== undefined) {
      query.category = categoryId;
    }
    return request.get<{ [id: string]: string }>({ url: `api/asset-lab/extensions`, query });
  },
);

export const loadAssetDetails = APIScenarioFactory(
  (assetId: Asset['id'], sharingId?: SharingLink['id']) =>
    request.get<AssetDetails>({
      url: sharingId
        ? `/api/asset-lab/sharing/assets/${assetId}?sharing_id=${sharingId}`
        : `/api/asset-lab/assets/${assetId}`,
    }),
);

export type LoadPackReturn = AssetsData & { asset_ids: Asset['id'][] | null };
export const loadPack = APIScenarioFactory(
  ({
    orderBy,
    ordering,
    offset,
  }: {
    orderBy?: string;
    ordering?: 'asc' | 'desc';
    offset?: number;
  }) => {
    const query: Record<string, string> = {};
    if (orderBy !== undefined) {
      query.order_by = orderBy;
    }
    if (ordering !== undefined) {
      query.ordering = ordering;
    }
    if (offset !== undefined) {
      query.offset = String(offset);
    }
    return request.get<LoadPackReturn>({ url: `/api/asset-lab/pack`, query });
  },
);

export const addToPack = APIScenarioFactory(
  ({ ids, filter }: { ids?: Asset['id'][]; filter?: LoadAssetsFilter }) =>
    request.post<AssetsTotalState>({
      url: `/api/asset-lab/pack/append`,
      payload: { asset_ids: ids, filter },
    }),
);

export const deleteFromPack = APIScenarioFactory(
  ({ ids, filter }: { ids?: Asset['id'][]; filter?: LoadAssetsFilter }) =>
    request.post<AssetsTotalState>({
      url: `/api/asset-lab/pack/delete`,
      payload: { asset_ids: ids, filter },
    }),
);

export const clearPack = APIScenarioFactory(() =>
  request.post({ url: `/api/asset-lab/pack/clear` }),
);

export const replacePackWithSharing = APIScenarioFactory(
  ({ sharingId }: { sharingId: SharingLink['id'] }) =>
    request.post({
      url: `/api/asset-lab/pack/update-from-sharing`,
      payload: { sharing_id: sharingId },
    }),
);

export const urlPackDownload = () => `/api/asset-lab/pack/download`;

export type LoadFavoritesReturn = AssetsData & { asset_ids: Asset['id'][] | null };
export const loadFavorites = APIScenarioFactory(
  ({
    orderBy,
    ordering,
    offset,
    ...rest
  }: {
    orderBy?: string;
    ordering?: 'asc' | 'desc';
    offset?: number;
  } & SearchApiParams) => {
    const query: Record<string, string | number> = { ...rest };
    if (orderBy !== undefined) {
      query.order_by = orderBy;
    }
    if (ordering !== undefined) {
      query.ordering = ordering;
    }
    if (offset !== undefined) {
      query.offset = String(offset);
    }
    return request.get<LoadFavoritesReturn>({ url: `/api/asset-lab/favorites`, query });
  },
);

export const addToFavorites = APIScenarioFactory(
  ({ ids, filter }: { ids?: Asset['id'][]; filter?: LoadAssetsFilter }) =>
    request.post<AssetsTotalState>({
      url: `/api/asset-lab/favorites/append`,
      payload: { asset_ids: ids, filter },
    }),
);

export const deleteFromFavorites = APIScenarioFactory(
  ({ ids, filter }: { ids?: Asset['id'][]; filter?: LoadAssetsFilter }) =>
    request.post<AssetsTotalState>({
      url: `/api/asset-lab/favorites/delete`,
      payload: { asset_ids: ids, filter },
    }),
);

export const clearFavorites = APIScenarioFactory(() =>
  request.post({ url: `/api/asset-lab/favorites/clear` }),
);

export type LoadSharingAssetsReturn = AssetsData & { sharing_link: SharingLink | null };
export const loadSharingAssets = APIScenarioFactory(
  ({
    sharingId,
    orderBy,
    ordering,
    offset,
    ...rest
  }: {
    sharingId: SharingLink['id'];
    orderBy?: string;
    ordering?: 'asc' | 'desc';
    offset?: number;
  } & SearchApiParams) => {
    const query: Record<string, string | number> = { ...rest };
    query.sharing_id = sharingId;
    if (orderBy !== undefined) {
      query.order_by = orderBy;
    }
    if (ordering !== undefined) {
      query.ordering = ordering;
    }
    if (offset !== undefined) {
      query.offset = String(offset);
    }
    return request.get<LoadSharingAssetsReturn>({ url: `/api/asset-lab/sharing/assets`, query });
  },
);

export const urlSharingDownload = (sharingId: SharingLink['id']) =>
  `/api/asset-lab/sharing/assets/download?sharing_id=${sharingId}`;

export const createSharing = APIScenarioFactory(
  ({
    assetIds,
    sharingId,
    filter,
  }: {
    assetIds?: Asset['id'][];
    sharingId?: SharingLink['id'];
    filter?: LoadAssetsFilter;
  } = {}) => {
    const payload = {
      ...(assetIds !== undefined ? { asset_ids: assetIds } : {}),
      ...(sharingId !== undefined ? { sharing_id: sharingId } : {}),
      ...(filter !== undefined ? { filter } : {}),
    };
    return request.post<SharingLink>({ url: `/api/asset-lab/sharing/create`, payload });
  },
);

export const updateSharingAssets = ({ sharingId }: { sharingId: SharingLink['id'] }) =>
  safe.post({
    url: `/api/asset-lab/sharing/update-assets`,
    payload: { sharing_id: sharingId },
  });

export const loadSharingLinks = APIScenarioFactory(
  ({
    is_active,
    ...query
  }: {
    ordering?: string;
    page?: number;
    search?: string;
    is_active?: boolean;
  }) =>
    request.get<{ items: SharingLink[]; total: number }>({
      url: `/api/asset-lab/sharing/links`,
      query: { ...query, is_active: is_active === undefined ? undefined : String(is_active) },
    }),
);

export const deleteSharingLinks = APIScenarioFactory((ids: SharingLink['id'][]) =>
  request.post({ url: `/api/asset-lab/sharing/delete`, payload: { ids } }),
);

export const restoreSharingLinks = APIScenarioFactory((ids: SharingLink['id'][]) =>
  request.post({ url: `/api/asset-lab/sharing/restore`, payload: { ids } }),
);

export type UpdateSharingLinkParams = Pick<SharingLink, 'id' | 'name'>;
export const updateSharingLink = APIScenarioFactory((params: UpdateSharingLinkParams) => {
  const { id, ...payload } = params;
  return request.post<SharingLink>({
    url: `/api/asset-lab/sharing/change`,
    query: { sharing_id: id },
    payload,
  });
});

// Ссылки на превью линкованных к ассету креативов

export const urlLinkedCreative = (
  mode: 'preview' | 'download',

  assetID: number,
  creativeID: number,
  sharingID?: string,
) => {
  const url = new URL(
    `/api/asset-lab/${sharingID ? 'sharing/' : ''}assets/${creativeID}/${mode}`,
    window.location.href,
  );
  if (sharingID) {
    url.searchParams.set('sharing_id', sharingID);
  }
  url.searchParams.set('related_id', String(assetID));
  return url.href;
};

export type LastStateFromServer = {
  id: number;
  state: unknown;
  created_at: string;
  last_use: string;
  views: number;
};

export const saveLastState = APIScenarioFactory((payload) =>
  request.post<LastStateFromServer>({ url: `/api/asset-lab/state`, payload }),
);

export const loadLastState = APIScenarioFactory((stateId: LastStateFromServer['id'] | null) => {
  const url = stateId === null ? `/api/asset-lab/state` : `/api/asset-lab/state/${stateId}`;
  return request.get<LastStateFromServer>({ url });
});

export const urlArchive = (assetId: Asset['id'], path: string) =>
  `/api/asset-lab/assets/${assetId}/archive/${path}`;

export const urlArchiveShare = (assetId: Asset['id'], path: string, sharingId: SharingLink['id']) =>
  `/api/asset-lab/sharing/assets/${assetId}/archive/${path}?sharing_id=${sharingId}`;

export const urlCompressedTreeProvider = (assetId: Asset['id'], sharingId?: SharingLink['id']) =>
  sharingId
    ? `/api/asset-lab/sharing/assets/${assetId}/archive?sharing_id=${sharingId}`
    : `/api/asset-lab/assets/${assetId}/archive`;

export const urlAssetLayer = (assetId: Asset['id'], sharingId?: SharingLink['id']) =>
  sharingId
    ? `/api/asset-lab/sharing/assets/${assetId}/layer?sharing_id=${sharingId}`
    : `/api/asset-lab/assets/${assetId}/layer`;

export const urlAssetLayerView = (
  assetId: Asset['id'],
  fileName: string,

  sharingId?: SharingLink['id'],
) => {
  const url = new URL(
    `/api/asset-lab/${sharingId ? 'sharing/' : ''}assets/${assetId}/layer/view/${fileName}`,
    window.location.origin,
  );
  if (sharingId) {
    url.searchParams.set('sharing_id', sharingId);
  }
  return url.href;
};

export const urlAssetLayerDownload = (
  assetId: Asset['id'],
  fileName: string,
  sharingId?: SharingLink['id'],
) => {
  const url = new URL(
    `/api/asset-lab/${sharingId ? 'sharing/' : ''}assets/${assetId}/layer/download/${fileName}`,
    window.location.origin,
  );
  if (sharingId) {
    url.searchParams.set('sharing_id', sharingId);
  }
  return url.href;
};

export const urlAssetLayerGroupDownload = (
  assetId: Asset['id'],
  layerId: number,
  isFullSize: boolean,

  sharingId?: SharingLink['id'],
) => {
  const url = new URL(
    `/api/asset-lab/${
      sharingId ? 'sharing/' : ''
    }assets/${assetId}/layer/download-group/${layerId}`,
    window.location.origin,
  );
  url.searchParams.set('fullsize', isFullSize ? '1' : '0');
  if (sharingId) {
    url.searchParams.set('sharing_id', sharingId);
  }
  return url.href;
};

export const deleteAssetLink = APIScenarioFactory((linkId: AssetLink['id']) =>
  request.delete({ url: `/api/asset-lab/links/${linkId}/` }),
);

export const createAssetLink = APIScenarioFactory((asset_id: Asset['id'], url: string) =>
  request.post<AssetLink>({ url: `/api/asset-lab/links/`, payload: { asset_id, url } }),
);

export const updateAssetLink = APIScenarioFactory((linkId: AssetLink['id'], url: string) =>
  request.patch<AssetLink>({ url: `/api/asset-lab/links/${linkId}/`, payload: { url } }),
);

export type Comment = {
  id: number;
  content: string;
  author: string;
  author_id: number;
  created_at: string;
  updated_at: string | null;
};

export const loadComments = APIScenarioFactory(
  (assetId: Asset['id'], sharingId?: SharingLink['id']) =>
    request.get<Comment[]>({
      url: `/api/asset-lab/comments/`,
      query: { asset_id: assetId, sharing_id: sharingId },
    }),
);

export const addComment = APIScenarioFactory((assetId: Asset['id'], content: Comment['content']) =>
  request.post({
    url: `/api/asset-lab/comments/`,
    payload: { asset_id: assetId, content },
  }),
);

export const deleteComment = APIScenarioFactory((commentId: Comment['id']) =>
  request.delete({ url: `/api/asset-lab/comments/${commentId}` }),
);

export const updateComment = APIScenarioFactory(
  (commentId: Comment['id'], content: Comment['content']) =>
    request.patch({ url: `/api/asset-lab/comments/${commentId}`, payload: { content } }),
);
